import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { getSortingOption } from 'Dashboard/components/ActionItemsSortingHeader/utils';
import ActionItemsSortingHeader from 'Dashboard/components/ActionItemsSortingHeader/ActionItemsSortingHeader';
import ActionItem from 'Shared/components/ActionItem/ActionItem';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { ItemType, WorkspaceItem, SelectedSort } from 'Shared/constants/types';
import WorkspaceItemParentInfo from 'Shared/components/WorkspaceItemParentInfo/WorkspaceItemParentInfo';
import { getNextOrPrevItem, PositionTypes } from 'Shared/utils/getNextOrPrevItem';

import 'Shared/styles/Animation.scss';

type Props = {
  forbidDrag?: boolean;
  actionItems: Array<WorkspaceItem>;
  hideActionsAreaOnDesktop?: boolean;
  selectedSort: SelectedSort;
  withWorkspaceSection?: boolean;
  withDueDateSection?: boolean;
  withOwnerSection?: boolean;
  setSelectedSort: Function;
  onActionItemDrop?: Function;
  onCreateItem?: Function;
  showTableHeaders?: boolean;
  forceShowMobileActions?: boolean;
  enableHideAnimation?: boolean;
};

function ActionItemsTable({
  actionItems,
  forbidDrag = false,
  withWorkspaceSection = true,
  withDueDateSection = true,
  withOwnerSection = false,
  hideActionsAreaOnDesktop = false,
  selectedSort,
  setSelectedSort,
  onActionItemDrop,
  onCreateItem,
  showTableHeaders = true,
  forceShowMobileActions = false,
  enableHideAnimation,
}: Props) {
  const dispatch = useDispatch();

  const handleSelectedItemUpdate = useCallback(
    itemUuid => dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true })),
    [dispatch]
  );

  const handleClickSortOption = useCallback(
    event => {
      const selectedIdSort = event.currentTarget.id;
      setSelectedSort({
        actionItems: selectedIdSort === 'actionItems' ? getSortingOption(selectedSort.actionItems) : 'default',
        owner: selectedIdSort === 'owner' ? getSortingOption(selectedSort.owner) : 'default',
        workspace: selectedIdSort === 'workspace' ? getSortingOption(selectedSort.workspace) : 'default',
        dueDate: selectedIdSort === 'dueDate' ? getSortingOption(selectedSort.dueDate) : 'default',
      });
    },
    [selectedSort, setSelectedSort]
  );

  const handleArrowUpClick = ({ uuid: currentItemUuid }) => {
    const prevItemUuid = getNextOrPrevItem(actionItems, currentItemUuid, PositionTypes.PREV);
    handleSelectedItemUpdate(prevItemUuid);
  };

  const handleArrowDownClick = ({ uuid: currentItemUuid }) => {
    const nextItemUuid = getNextOrPrevItem(actionItems, currentItemUuid, PositionTypes.NEXT);
    handleSelectedItemUpdate(nextItemUuid);
  };

  const handleAddActionItem = useCallback(
    sourceItemUuid => {
      if (onCreateItem) {
        const itemUuid = uuidv4();
        onCreateItem({ sourceItemUuid, itemUuid });
        handleSelectedItemUpdate(itemUuid);
      }
    },
    [handleSelectedItemUpdate, onCreateItem]
  );

  const hasHighPriorityItems = actionItems.some(({ isHighPriority }) => isHighPriority);
  const firstItemUuid = actionItems.find(({ isHighPriority }) => !isHighPriority)?.uuid;
  const hasSorting = Boolean(Object.keys(selectedSort).find(option => selectedSort[option] !== 'default'));
  const lastItemUuid = actionItems[actionItems.length - 1]?.uuid || null;

  return (
    <ActionItemsSortingHeader
      withWorkspaceSection={withWorkspaceSection}
      withDueDateSection={withDueDateSection}
      withOwnerSection={withOwnerSection}
      selectedSort={selectedSort}
      inUppercase={false}
      onClickSortOption={handleClickSortOption}
      showTableHeaders={showTableHeaders}
    >
      <div className="border-top">
        {actionItems.map(({ uuid, parentUuid, workspacePath, parentText }) => (
          <ActionItem
            enableHideAnimation={enableHideAnimation}
            notShowAsChild
            withWorkspaceSection={withWorkspaceSection}
            withDueDateSection={withDueDateSection}
            withOwnerSection={withOwnerSection}
            key={uuid}
            forbidDrag={hasSorting || forbidDrag}
            uuid={uuid}
            isFirst={firstItemUuid === uuid}
            isLast={uuid === lastItemUuid}
            hasHighPriorityItems={!hasSorting && hasHighPriorityItems}
            onArrowUpClick={handleArrowUpClick}
            onArrowDownClick={handleArrowDownClick}
            hideActionsAreaOnDesktop={hideActionsAreaOnDesktop}
            selector="meetingItems"
            parentItemInfoComponent={
              parentUuid ? (
                <div className="ml-4">
                  <WorkspaceItemParentInfo
                    parentUuid={parentUuid}
                    workspacePath={workspacePath}
                    parentText={parentText}
                    itemType={ItemType.actionItem}
                  />
                </div>
              ) : null
            }
            onDrop={onActionItemDrop}
            onAddItem={handleAddActionItem}
            forceShowMobileActions={forceShowMobileActions}
          />
        ))}
      </div>
    </ActionItemsSortingHeader>
  );
}

export default ActionItemsTable;
